import { cn } from "@/lib/utils";
import type { TrackRecordScope } from "@/types/track-record.types";
import { pct, testedCommitmentsLabel } from "./format";

interface TrackRecordSummaryProps {
  scope: TrackRecordScope;
}

// Spec 079 (US1) — os quatro números que respondem "dá para levar o gêmeo a sério?".
//
// Dois deles são medidas do MODELO (acurácia, acerto da regra de proposta) e dois são medidas do
// PROCESSO (não testado, aguardando julgamento). Os quatro juntos, porque suprimir os dois últimos
// faria a régua esconder que o problema é de execução ou de fila parada — e não do modelo (FR-003).
//
// ⚠️ Cada número leva o TAMANHO DA AMOSTRA ao lado. "41%" sobre 3 compromissos e "41%" sobre 300
// pedem decisões opostas, e um percentual sem denominador não distingue os dois.

interface NumberBlockProps {
  label: string;
  value: string;
  hint: string;
  /** Destaque: só a acurácia. Se tudo tem o mesmo peso visual, nada tem. */
  accent?: boolean;
}

function NumberBlock({ label, value, hint, accent = false }: NumberBlockProps) {
  return (
    <div className="flex min-w-0 flex-col gap-1 border-r border-border-subtle px-4 py-3 last:border-r-0">
      <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-text-secondary">
        {label}
      </span>
      <span
        className={cn(
          "font-mono tabular-nums leading-[1.1] tracking-[-0.02em] text-text-primary",
          accent ? "text-[28px] font-medium" : "text-[20px]"
        )}
      >
        {value}
      </span>
      <span className="text-[11px] leading-tight text-text-tertiary">{hint}</span>
    </div>
  );
}

export function TrackRecordSummary({ scope }: TrackRecordSummaryProps) {
  return (
    <div className="grid grid-cols-2 overflow-hidden rounded-[14px] border border-border-subtle bg-bg-surface md:grid-cols-4">
      <NumberBlock
        accent
        label="Acurácia"
        value={pct(scope.accuracy)}
        // A amostra é o denominador REAL: só os testados. Não confundir com o total apurado.
        // ⚠️ A concordância vem PRONTA do helper: `${n} compromisso` + " testados" produzia
        // "sobre 1 compromisso testados" no primeiro dia de uso de toda fábrica.
        hint={`sobre ${testedCommitmentsLabel(scope.sampleSize)}`}
      />
      <NumberBlock
        label="Acerto da proposta"
        value={pct(scope.proposalAccuracy)}
        // ⚠️ O denominador é TODA classificação vigente, inclusive aquelas em que a regra NÃO SOUBE
        // propor — "não soube" conta como erro da regra, que é o veredito honesto sobre ela. A frase
        // anterior ("quando a regra propôs a causa, ela acertou") descrevia um denominador menor, e
        // lida assim um acerto de 32% parecia uma regra que erra o dobro do que erra.
        hint="das causas julgadas, a regra tinha proposto a certa"
      />
      <NumberBlock
        label="Não testados"
        value={scope.untestedCount.toLocaleString("pt-BR")}
        // FR-002 — a fábrica não rodou o que foi comprometido. O modelo não foi posto à prova, e o
        // número alto aqui é um problema de execução, nunca do gêmeo.
        hint="a fábrica não seguiu o combinado"
      />
      <NumberBlock
        label="Aguardando julgamento"
        value={scope.awaitingJudgmentCount.toLocaleString("pt-BR")}
        hint="apurados que ninguém classificou ainda"
      />
    </div>
  );
}
